import React, { Fragment, memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'src/components/shared/Button'
import { CommonModal } from 'src/components/shared/CommonModal'
import { ConfirmActionModal } from 'src/components/shared/ConfirmActionModal'
import { BasicCheckbox } from 'src/components/shared/form/BasicCheckbox'
import {
  AdminAssignTransactionInvoice,
  AdminFetchPartnerPendingInvoices,
} from 'src/constants/actionTypes'
import { transactionPaymentTypeLabels } from 'src/constants/payments-ops/labels'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import type {
  AssignInvoiceResult,
  FetchMissingInvoiceTransactionsPayload,
  PaymentsOpsTransaction,
} from 'src/interfaces/transactionsReview'
import {
  getPartnerPendingInvoices,
  getPartnerPendingInvoicesError,
  getPartnerPendingInvoicesLoading,
  getPartnerPendingInvoicesPartnerId,
  getTransactionsReviewSubmitting,
} from 'src/selectors/transactionsReview'
import { formatCentsAsUsd } from 'src/utils/invoiceMoney'
import { usePartnerNameMap } from 'src/utils/hooks/paymentsOps/usePartnerNameMap'
import { compareTransactionToInvoice } from 'src/utils/paymentsOps/invoiceMatch'
import { resolvePartnerName } from 'src/utils/paymentsOps/partnerNames'
import { InvoiceMatchPanel } from './InvoiceMatchPanel'
import { InvoiceOptionCard } from './InvoiceOptionCard'

interface Props {
  /** `null` closes the modal. */
  transaction: PaymentsOpsTransaction | null
  /** The list request to replay after a successful assign. */
  refetch: FetchMissingInvoiceTransactionsPayload
  onClose(): void
}

const AssignInvoiceModalComponent = ({ transaction, refetch, onClose }: Props) => {
  const dispatch = useDispatch()
  const snackbar = useSnackbar()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()
  const nameMap = usePartnerNameMap()

  const invoices = useSelector(getPartnerPendingInvoices)
  const invoicesPartnerId = useSelector(getPartnerPendingInvoicesPartnerId)
  const isInvoicesLoading = useSelector(getPartnerPendingInvoicesLoading)
  const invoicesError = useSelector(getPartnerPendingInvoicesError)
  const isSubmitting = useSelector(getTransactionsReviewSubmitting)

  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null)
  const [isAcknowledged, setIsAcknowledged] = useState<boolean>(false)
  const [isConfirming, setIsConfirming] = useState<boolean>(false)
  // The transaction whose invoice request this modal has fired. Until it matches the open
  // transaction the store still holds the PREVIOUS open's invoices (a same-partner reopen keeps
  // them), so nothing is shown from the store before this modal's own request is in flight.
  const [requestedTransactionId, setRequestedTransactionId] = useState<string | null>(null)

  const transactionId = transaction?.id ?? null
  const partnerId = transaction?.partnerId ?? null

  useEffect(() => {
    setSelectedInvoiceId(null)
    setIsAcknowledged(false)
    setIsConfirming(false)

    if (!partnerId || !transactionId) {
      setRequestedTransactionId(null)

      return
    }

    dispatch(AdminFetchPartnerPendingInvoices.request({ partnerId }))
    setRequestedTransactionId(transactionId)
  }, [dispatch, partnerId, transactionId])

  const isInvoicesReady =
    partnerId !== null &&
    requestedTransactionId === transactionId &&
    invoicesPartnerId === partnerId &&
    !isInvoicesLoading

  // Never show another partner's (or a stale) list while this partner's loads, nor any list next
  // to a load error — what is in the store then is not this open's answer.
  const partnerInvoices = useMemo(() => {
    return isInvoicesReady && !invoicesError ? invoices : []
  }, [invoices, invoicesError, isInvoicesReady])

  const selectedInvoice = useMemo(() => {
    return (
      partnerInvoices.find((invoice) => {
        return invoice.id === selectedInvoiceId
      }) ?? null
    )
  }, [partnerInvoices, selectedInvoiceId])

  const match = useMemo(() => {
    if (!transaction || !selectedInvoice) {
      return null
    }

    return compareTransactionToInvoice(transaction.amount, selectedInvoice)
  }, [selectedInvoice, transaction])

  const handleSelect = useCallback((invoiceId: string) => {
    setSelectedInvoiceId(invoiceId)
    setIsAcknowledged(false)
  }, [])

  const handleAcknowledgementChange = useCallback((event: React.ChangeEvent<any>) => {
    setIsAcknowledged(Boolean(event.target.checked))
  }, [])

  // A write in flight must finish (and be reported) before the modal can go away.
  const handleClose = useCallback(() => {
    if (isSubmitting) {
      return
    }

    onClose()
  }, [isSubmitting, onClose])

  const handleOpenConfirm = useCallback(() => {
    setIsConfirming(true)
  }, [])

  const handleCloseConfirm = useCallback(() => {
    if (isSubmitting) {
      return
    }

    setIsConfirming(false)
  }, [isSubmitting])

  const handleConfirm = useCallback(async () => {
    if (!transaction || !selectedInvoice) {
      return
    }

    const result = await asyncDispatchWithErrorToast<AssignInvoiceResult>(
      AdminAssignTransactionInvoice.request({
        transactionId: transaction.id,
        invoiceId: selectedInvoice.id,
        refetch,
      }),
    )

    // Neither modal can be dismissed while the request runs, so both are still open here.
    if (!result) {
      setIsConfirming(false)

      return
    }

    const messages = result.messages ?? []

    snackbar?.enqueueSnackbar(messages.length > 0 ? messages.join(' ') : 'Invoice assigned', {
      variant: messages.length > 0 ? 'info' : 'success',
    })
    onClose()
  }, [asyncDispatchWithErrorToast, onClose, refetch, selectedInvoice, snackbar, transaction])

  if (!transaction) {
    return null
  }

  const amount = formatCentsAsUsd(transaction.amount)
  const partnerName = resolvePartnerName(nameMap, transaction.partnerId, transaction.partnerName)
  const paymentType = transaction.paymentType
    ? transactionPaymentTypeLabels[transaction.paymentType]
    : '—'
  const isMismatch = match !== null && match.kind !== 'match'
  const invoiceLabel = selectedInvoice
    ? selectedInvoice.invoiceId ?? selectedInvoice.partnerComposedInvoiceId
    : ''
  const canSubmit =
    isInvoicesReady && Boolean(selectedInvoice) && (!isMismatch || isAcknowledged) && !isSubmitting
  const isWaitingForInvoices = partnerId !== null && !isInvoicesReady

  let acknowledgementLabel = ''

  if (match?.kind === 'overfund') {
    acknowledgementLabel = `I understand this overfunds ${invoiceLabel} by ${formatCentsAsUsd(
      match.differenceCents,
    )}.`
  }

  if (match?.kind === 'underfund') {
    acknowledgementLabel = `I understand this only partially pays ${invoiceLabel} (short ${formatCentsAsUsd(
      match.differenceCents,
    )}).`
  }

  return (
    <Fragment>
      <CommonModal
        isOpen={!isConfirming}
        title="Assign to invoice"
        className={tw`max-w-[560px]`}
        onClose={handleClose}
        buttons={
          <Fragment>
            <Button size="small" color="grey" disabled={isSubmitting} onClick={handleClose}>
              Cancel
            </Button>
            <Button
              size="small"
              color="dark"
              disabled={!canSubmit}
              onClick={handleOpenConfirm}
              data-testid="assign-invoice-submit"
            >
              {`Assign ${amount}…`}
            </Button>
          </Fragment>
        }
      >
        <div css={tw`flex flex-col gap-4`}>
          <Typography variant="body2">
            Incoming {amount} · {paymentType} · {transaction.id}
          </Typography>
          {!partnerId ? (
            <Typography variant="body2" css={tw`text-critical-500`}>
              This transaction has no partner, so no invoices can be listed.
            </Typography>
          ) : null}
          {isWaitingForInvoices ? (
            <Typography variant="body2" css={tw`text-shades-500`}>
              Loading open invoices…
            </Typography>
          ) : null}
          {isInvoicesReady && invoicesError ? (
            <Typography variant="body2" css={tw`text-critical-500`}>
              {invoicesError}
            </Typography>
          ) : null}
          {isInvoicesReady && !invoicesError && partnerInvoices.length === 0 ? (
            <Typography variant="body2" css={tw`text-shades-500`}>
              No pending invoices for this partner.
            </Typography>
          ) : null}
          <div role="radiogroup" aria-label="Pending invoices" css={tw`flex flex-col gap-2`}>
            {partnerInvoices.map((invoice) => {
              return (
                <InvoiceOptionCard
                  key={invoice.id}
                  invoice={invoice}
                  isSelected={invoice.id === selectedInvoiceId}
                  onSelect={handleSelect}
                />
              )
            })}
          </div>
          {match ? (
            <InvoiceMatchPanel transactionAmount={transaction.amount} match={match} />
          ) : null}
          {isMismatch ? (
            <BasicCheckbox
              name="assignInvoiceAcknowledgement"
              label={acknowledgementLabel}
              value={isAcknowledged}
              onChange={handleAcknowledgementChange}
              inputProps={{ 'data-testid': 'assign-invoice-acknowledgement' }}
            />
          ) : null}
        </div>
      </CommonModal>
      <ConfirmActionModal
        isOpen={isConfirming}
        title="Assign transaction?"
        description={`Assign ${amount} from ${partnerName} to ${invoiceLabel} (remaining ${formatCentsAsUsd(
          match?.remainingCents ?? 0,
        )})? This cannot be undone.`}
        confirmLabel={`Assign ${amount}`}
        isSubmitting={isSubmitting}
        onConfirm={handleConfirm}
        onClose={handleCloseConfirm}
        confirmDataTestId="assign-invoice-confirm"
      />
    </Fragment>
  )
}

export const AssignInvoiceModal = memo(AssignInvoiceModalComponent)
